import clsx from 'clsx';
import React from 'react';
import { toggleSwitch } from '../../reducers/switch';
import { useDispatch } from 'react-redux';
import ToggleIcon from '../../assets/icons/sidebar-toggle-icon.svg?react';
import Input from '../../components/Input';
import Select from '../../components/Select';
import Button from '../../components/Button';
import { useButtonSizeSmaller } from '../../utils/buttonSizes';
import ChevronIcon from '../../assets/icons/chevron-down-icon.svg?react';
import { CompanySize, IndustryType } from '@EHSNavigator/backend';
import Checkbox from '../../components/Checkbox';

interface OnboardOrganizationStepTwoProps {
  otherClasses?: string;
  formHandler: any;
  handleSteps: React.Dispatch<number>;
}

const OnboardOrganizationStepTwo: React.FC<OnboardOrganizationStepTwoProps> = ({
  otherClasses,
  formHandler,
  handleSteps,
}) => {
  const onboardOrganizationStepTwoClasses = clsx(otherClasses);
  const dispatch = useDispatch();

  return (
    <div
      className={onboardOrganizationStepTwoClasses}
      data-testid="onboard-organization-step-one"
    >
      <div className="flex items-center justify-between">
        <div className="flex max-w-818 items-start gap-2.5">
          <button
            className="bg-translucent-background/80 border-neutral-tones-text/30 rounded-5 cursor-pointer border p-0.5 lg:hidden"
            onClick={() => dispatch(toggleSwitch())}
          >
            <ToggleIcon />
          </button>
          <div>
            <h1 className="h3">Onboard Organization</h1>
            <p className="font-inter-variable text-neutral-tones-text/80 -tracking-button mt-15 hidden text-xl leading-6 font-medium lg:block">
              Create a new organization in EHS Navigator
            </p>
          </div>
        </div>
      </div>

      <div className="mt-15 mb-8 flex w-full max-w-955 flex-col gap-6 lg:mt-12">
        <p className="font-inter-variable text-neutral-tones-text/80 -tracking-button -mb-1 text-lg leading-5 font-medium lg:hidden">
          Create a new organization in EHS Navigator
        </p>
        <h6 className="text-brand-byzantine font-inter-variable -tracking-button text-xl leading-6 font-bold">
          Organization Details
        </h6>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <Input
            onBlur={formHandler?.handleBlur}
            label="Company Name*"
            errorMessage={
              formHandler?.touched.companyName &&
                formHandler?.errors.companyName
                ? formHandler?.errors.companyName
                : ''
            }
            disabled={formHandler?.isSubmitting}
            value={formHandler?.values.companyName}
            type="text"
            placeholder="e.g. Globex ltd"
            onChange={formHandler?.handleChange}
            name="companyName"
            otherClasses={{ container: 'w-full' }}
            autoComplete="new-password"
          />
          <Select
            label="Industry Type*"
            list={Object.entries(IndustryType).map(([, value]) => ({
              label: value,
              value: value,
            }))}
            value={formHandler.values.industryType}
            disabled={formHandler.isSubmitting}
            placeholder="Industry Types"
            otherClasses={{ container: 'w-full' }}
            onSelect={(e) =>
              formHandler?.setFieldValue('industryType', e, true)
            }
            errorMessage={
              formHandler?.touched.industryType &&
                formHandler?.errors.industryType
                ? formHandler?.errors.industryType
                : ''
            }
          />
        </div>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <Select
            label="Industry Size*"
            list={Object.entries(CompanySize).map(([, value]) => ({
              label: value,
              value: value,
            }))}
            value={formHandler.values.industrySize}
            disabled={formHandler.isSubmitting}
            placeholder="e.g. 301+ employees"
            otherClasses={{ container: 'w-full' }}
            onSelect={(e) =>
              formHandler?.setFieldValue('industrySize', e, true)
            }
            errorMessage={
              formHandler?.touched.industrySize &&
                formHandler?.errors.industrySize
                ? formHandler?.errors.industrySize
                : ''
            }
          />
          <Input
            onBlur={formHandler?.handleBlur}
            label="Headquarter Address*"
            errorMessage={
              formHandler?.touched.headquarterAddress &&
                formHandler?.errors.headquarterAddress
                ? formHandler?.errors.headquarterAddress
                : ''
            }
            disabled={formHandler?.isSubmitting}
            value={formHandler?.values.headquarterAddress}
            type="text"
            placeholder="e.g. California"
            onChange={formHandler?.handleChange}
            name="headquarterAddress"
            otherClasses={{ container: 'w-full' }}
            autoComplete="new-password"
          />
        </div>
        <div className="flex w-full justify-start">
          <Input
            onBlur={formHandler?.handleBlur}
            label={
              <>
                Organization Contact Number{' '}
                <em className="text-sm font-normal">(Optional)</em>
              </>
            }
            errorMessage={
              formHandler?.touched.organizationContact &&
                formHandler?.errors.organizationContact
                ? formHandler?.errors.organizationContact
                : ''
            }
            disabled={formHandler?.isSubmitting}
            value={formHandler?.values.organizationContact}
            type="tel"
            placeholder="Enter organization contact number"
            onChange={formHandler?.handleChange}
            name="organizationContact"
            otherClasses={{ container: 'w-full max-w-465' }}
            autoComplete="new-password"
          />
        </div>


        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <Input
            onBlur={formHandler?.handleBlur}
            label="License Count*"
            errorMessage={
              formHandler?.touched.licenseCount && formHandler?.errors.licenseCount
                ? formHandler?.errors.licenseCount
                : ''
            }
            disabled={formHandler?.isSubmitting}
            value={formHandler?.values.licenseCount}
            type="text"
            placeholder="e.g. 10"
            onChange={(e) => {
              const value = e.target.value;
              if (value === '' || /^\d+$/.test(value)) {
                formHandler?.setFieldValue('licenseCount', value, true);
              }
            }}
            name="licenseCount"
            otherClasses={{ container: 'w-full' }}
            autoComplete="new-password"
          />

          <Input
            onBlur={formHandler?.handleBlur}
            label="Amount (Optional)"
            errorMessage={
              formHandler?.touched.amount && formHandler?.errors.amount
                ? formHandler?.errors.amount
                : ''
            }
            disabled={formHandler?.isSubmitting}
            value={formHandler?.values.amount}
            type="number"
            placeholder="e.g. 1000"
            onChange={(e) => {
              const value = e.target.value;
              if (value === '' || /^\d+(\.\d{0,2})?$/.test(value)) {
                formHandler?.setFieldValue('amount', value, true);
              }
            }}
            name="amount"
            otherClasses={{ container: 'w-full max-w-465' }}
            autoComplete="new-password"
          />
        </div>
        <div className="flex items-center mt-4">
          <Checkbox
            checked={formHandler?.values?.isPaid || false}
            name="isPaid"
            otherClasses="w-fit"
            onChange={(e) => {
              formHandler?.setFieldValue('isPaid', e.target.checked, true);
            }}
            onBlur={formHandler?.handleBlur}
            disabled={formHandler?.isSubmitting}
            errorMessage={
              formHandler?.touched.isPaid && formHandler?.errors.isPaid
                ? formHandler?.errors.isPaid
                : ''
            }
            label="Payment Completed"
          />
        </div>
      </div>

      <div className="mt-8 flex items-center gap-8">
        <Button
          onClick={() => handleSteps(1)}
          size={useButtonSizeSmaller()}
          type="button"
          variant="secondary"
          otherClasses="max-w-211 w-full"
        >
          <ChevronIcon className="h-5 w-5 rotate-90 [&>path]:fill-current" />{' '}
          Go Back
        </Button>
        <Button
          type="button"
          size={useButtonSizeSmaller()}
          onClick={() => handleSteps(3)}
          otherClasses="max-w-211 w-full"
        >
          Continue
          <ChevronIcon className="h-5 w-5 -rotate-90 [&>path]:fill-current" />
        </Button>
      </div>
    </div>
  );
};

export default OnboardOrganizationStepTwo;
